/**
 * Categories API Service
 * Handles all category-related API calls
 * Based on SPECIFICATION.md Section 7.8
 */
import api from './api';
import type {
  Category,
  CategoryWithChildren,
  CategoryTree,
  CategoryCreateRequest,
  CategoryUpdateRequest,
  CategoryListParams,
  CategoryStats,
  CategoryUsageSummary,
  FlattenedCategory,
  CategoryType,
  CategoryWithPreference,
  PreferenceUpdateResponse,
  BulkPreferenceUpdateItem,
} from '@/types/category';
import type { components } from '@/types/api.generated';

// ============================================================================
// API Response Types
// Backend returns camelCase (via APISchema with serialize_by_alias=True)
// ============================================================================

type ApiCategory = components['schemas']['CategoryResponse'];
type ApiCategoryTreeNode = components['schemas']['CategoryTreeNode'];
type ApiCategoryTreeResponse = components['schemas']['CategoryTreeResponse'];

// API response type for category with preference (WP-CAT-11)
// Schema: services/core/app/schemas/category.py CategoryWithPreferenceResponse
interface ApiCategoryWithPreference {
  id: string;
  name: string;
  type: string;
  parentId?: string | null;
  parent_id?: string | null; // Fallback for snake_case
  icon?: string | null;
  color?: string | null;
  displayOrder: number;
  display_order?: number; // Fallback for snake_case
  isSystem: boolean;
  is_system?: boolean; // Fallback for snake_case
  isEnabled: boolean;
  is_enabled?: boolean; // Fallback for snake_case
  createdAt: string;
  created_at?: string; // Fallback for snake_case
  updatedAt: string;
  updated_at?: string; // Fallback for snake_case
}

// API response type for preference update operations
interface ApiPreferenceUpdateResponse {
  status: string;
  count?: number;
  categoriesConfigured?: number;
  categories_configured?: number; // Fallback for snake_case
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Raw API response with snake_case fields.
 * Internal type - transformed before returning to consumers.
 */
interface ApiCategoriesListResponse {
  categories: ApiCategory[];
}

export interface CategoriesListResponse {
  categories: Category[];
}

export interface CategoryTreeResponse {
  tree: CategoryTree;
}

// ============================================================================
// Transformation Functions
// ============================================================================

/**
 * Transform API response to frontend type.
 *
 * Backend returns camelCase (via APISchema with alias_generator=to_camel).
 * We still support snake_case fallback for backwards compatibility or edge cases.
 *
 * Field mappings:
 * - API: displayOrder (camelCase) -> Frontend: sortOrder
 * - API: isSystem (camelCase) -> Frontend: isSystem
 * - API: parentId (camelCase) -> Frontend: parentId
 *
 * Fields not in API response (computed client-side):
 * - tenantId: Inferred from isSystem (null for system categories)
 * - path: Computed client-side from parent hierarchy
 * - depth: Computed client-side from parent hierarchy
 */
function transformCategory(apiCategory: ApiCategory): Category {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cat = apiCategory as any;

  // Handle isSystem with explicit boolean check (API returns camelCase)
  // Use ?? to only fall back when value is null/undefined, not when false
  const isSystem: boolean = cat.isSystem ?? cat.is_system ?? false;

  // Handle sortOrder mapping (API field is displayOrder)
  // Default to 0 if neither field is present
  const sortOrder: number = cat.displayOrder ?? cat.display_order ?? 0;

  return {
    id: cat.id,
    // tenantId not exposed by API - infer from isSystem
    tenantId: isSystem ? null : 'user',
    name: cat.name,
    // parentId can be null for root categories
    parentId: cat.parentId ?? cat.parent_id ?? undefined,
    icon: cat.icon ?? undefined,
    color: cat.color ?? undefined,
    isSystem,
    type: cat.type,
    // path and depth are computed client-side, not returned by API
    path: undefined,
    depth: undefined,
    sortOrder,
    createdAt: cat.createdAt ?? cat.created_at ?? '',
    updatedAt: cat.updatedAt ?? cat.updated_at ?? '',
  };
}

/**
 * Transform an array of API categories to frontend types.
 */
function transformCategories(apiCategories: ApiCategory[]): Category[] {
  return apiCategories.map(transformCategory);
}

/**
 * Transform a CategoryTreeNode (with nested children) to CategoryWithChildren.
 * Recursively transforms all children.
 *
 * Uses same field mapping as transformCategory:
 * - API: displayOrder (camelCase) -> Frontend: sortOrder
 * - API: isSystem (camelCase) -> Frontend: isSystem
 * - API: parentId (camelCase) -> Frontend: parentId
 */
function transformCategoryTreeNode(node: ApiCategoryTreeNode): CategoryWithChildren {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n = node as any;

  // Handle isSystem with explicit boolean check (API returns camelCase)
  const isSystem: boolean = n.isSystem ?? n.is_system ?? false;

  // Handle sortOrder mapping (API field is displayOrder)
  const sortOrder: number = n.displayOrder ?? n.display_order ?? 0;

  return {
    id: n.id,
    tenantId: isSystem ? null : 'user',
    name: n.name,
    parentId: n.parentId ?? n.parent_id ?? undefined,
    icon: n.icon ?? undefined,
    color: n.color ?? undefined,
    isSystem,
    // API returns string but we know it's always a valid CategoryType
    type: n.type as CategoryType,
    path: undefined,
    depth: undefined,
    sortOrder,
    // Tree nodes don't have createdAt/updatedAt, use empty string as fallback
    createdAt: '',
    updatedAt: '',
    children: n.children?.map(transformCategoryTreeNode),
  };
}

/**
 * Transform an array of tree nodes.
 */
function transformCategoryTreeNodes(nodes: ApiCategoryTreeNode[]): CategoryWithChildren[] {
  return nodes.map(transformCategoryTreeNode);
}

/**
 * Transform API response to CategoryWithPreference type.
 *
 * Backend returns camelCase (via APISchema with alias_generator=to_camel).
 * We still support snake_case fallback for backwards compatibility.
 *
 * Schema source: services/core/app/schemas/category.py CategoryWithPreferenceResponse
 * Field mappings:
 * - API: isEnabled (camelCase) -> Frontend: isEnabled
 * - API: isSystem (camelCase) -> Frontend: isSystem
 * - API: displayOrder (camelCase) -> Frontend: displayOrder
 */
function transformCategoryWithPreference(apiCat: ApiCategoryWithPreference): CategoryWithPreference {
  // Use ?? to handle false correctly (don't use || for booleans!)
  const isEnabled: boolean = apiCat.isEnabled ?? apiCat.is_enabled ?? true;
  const isSystem: boolean = apiCat.isSystem ?? apiCat.is_system ?? false;
  const displayOrder: number = apiCat.displayOrder ?? apiCat.display_order ?? 0;

  return {
    id: apiCat.id,
    name: apiCat.name,
    type: apiCat.type as CategoryType,
    parentId: apiCat.parentId ?? apiCat.parent_id ?? undefined,
    icon: apiCat.icon ?? undefined,
    color: apiCat.color ?? undefined,
    displayOrder,
    isSystem,
    isEnabled,
    createdAt: apiCat.createdAt ?? apiCat.created_at ?? '',
    updatedAt: apiCat.updatedAt ?? apiCat.updated_at ?? '',
  };
}

/**
 * Transform array of categories with preferences.
 */
function transformCategoriesWithPreferences(apiCats: ApiCategoryWithPreference[]): CategoryWithPreference[] {
  return apiCats.map(transformCategoryWithPreference);
}

/**
 * Transform API preference update response.
 */
function transformPreferenceUpdateResponse(apiResp: ApiPreferenceUpdateResponse): PreferenceUpdateResponse {
  return {
    status: apiResp.status,
    count: apiResp.count,
    categoriesConfigured: apiResp.categoriesConfigured ?? apiResp.categories_configured,
  };
}

// ============================================================================
// Categories Service
// ============================================================================

export const categoriesService = {
  /**
   * Get all categories (flat list)
   */
  getAll: async (params: CategoryListParams = {}): Promise<Category[]> => {
    const queryParams: Record<string, unknown> = {};

    if (params.type) queryParams.type = params.type;
    if (params.includeSystem !== undefined) queryParams.includeSystem = params.includeSystem;
    if (params.parentId) queryParams.parentId = params.parentId;
    if (params.flat !== undefined) queryParams.flat = params.flat;

    const response = await api.get<ApiCategoriesListResponse>('/categories', queryParams);
    return transformCategories(response.categories);
  },

  /**
   * Get category tree (hierarchical structure)
   */
  getTree: async (includeSystem: boolean = true): Promise<CategoryTree> => {
    const response = await api.get<ApiCategoryTreeResponse>('/categories/tree', {
      includeSystem,
    });
    // The tree response is an array of root nodes that need transformation
    // The CategoryTree type expects { income, expense, transfer } structure
    // but the API returns a flat array of tree nodes.
    // Transform the nodes and group by type.
    const allNodes = transformCategoryTreeNodes(response.tree);
    return {
      income: allNodes.filter(n => n.type === 'income'),
      expense: allNodes.filter(n => n.type === 'expense'),
      transfer: allNodes.filter(n => n.type === 'transfer'),
    };
  },

  /**
   * Get a single category by ID
   */
  getById: async (id: string): Promise<Category> => {
    const response = await api.get<ApiCategory>(`/categories/${id}`);
    return transformCategory(response);
  },

  /**
   * Create a new custom category
   */
  create: async (data: CategoryCreateRequest): Promise<Category> => {
    const response = await api.post<ApiCategory>('/categories', data);
    return transformCategory(response);
  },

  /**
   * Update a category
   */
  update: async (id: string, data: CategoryUpdateRequest): Promise<Category> => {
    const response = await api.patch<ApiCategory>(`/categories/${id}`, data);
    return transformCategory(response);
  },

  /**
   * Delete a custom category
   * Note: System categories cannot be deleted
   */
  delete: async (id: string): Promise<void> => {
    return api.delete(`/categories/${id}`);
  },

  // ============================================================================
  // Category Statistics
  // ============================================================================

  /**
   * Get usage statistics for a category
   */
  getStats: async (categoryId: string): Promise<CategoryStats> => {
    return api.get<CategoryStats>(`/categories/${categoryId}/stats`);
  },

  /**
   * Get overall category usage summary
   */
  getUsageSummary: async (): Promise<CategoryUsageSummary> => {
    return api.get<CategoryUsageSummary>('/categories/usage-summary');
  },

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Get flattened list of categories with full paths
   * Useful for dropdowns and selection lists
   */
  getFlattened: async (type?: CategoryType): Promise<FlattenedCategory[]> => {
    const categories = await categoriesService.getAll({
      type,
      includeSystem: true,
      flat: true,
    });

    return categories.map((cat) => ({
      id: cat.id,
      name: cat.name,
      // path is computed client-side if needed, fallback to name
      fullPath: cat.path ?? cat.name,
      // depth defaults to 0 if not computed
      depth: cat.depth ?? 0,
      type: cat.type,
      icon: cat.icon,
      color: cat.color,
    }));
  },

  /**
   * Get income categories only
   */
  getIncomeCategories: async (): Promise<Category[]> => {
    return categoriesService.getAll({ type: 'income', includeSystem: true });
  },

  /**
   * Get expense categories only
   */
  getExpenseCategories: async (): Promise<Category[]> => {
    return categoriesService.getAll({ type: 'expense', includeSystem: true });
  },

  /**
   * Get transfer categories only
   */
  getTransferCategories: async (): Promise<Category[]> => {
    return categoriesService.getAll({ type: 'transfer', includeSystem: true });
  },

  /**
   * Get custom (user-created) categories only
   */
  getCustomCategories: async (): Promise<Category[]> => {
    return categoriesService.getAll({ includeSystem: false });
  },

  /**
   * Get child categories of a parent
   */
  getChildren: async (parentId: string): Promise<Category[]> => {
    return categoriesService.getAll({ parentId });
  },

  /**
   * Find category by path (e.g., "expenses/food/restaurants")
   */
  findByPath: async (path: string): Promise<Category | null> => {
    const categories = await categoriesService.getAll({ flat: true });
    return categories.find((cat) => cat.path === path) || null;
  },

  /**
   * Move a category to a new parent
   */
  moveToParent: async (id: string, newParentId: string | null): Promise<Category> => {
    return categoriesService.update(id, { parentId: newParentId ?? undefined });
  },

  /**
   * Reorder categories within a parent
   */
  reorder: async (categoryIds: string[]): Promise<void> => {
    await api.post('/categories/reorder', { categoryIds });
  },

  /**
   * Suggest a category for a transaction based on name
   */
  suggest: async (
    transactionName: string,
    merchantName?: string
  ): Promise<{
    category: Category | null;
    confidence: number;
    ruleId?: string;
    ruleName?: string;
  }> => {
    const response = await api.post<{
      category: ApiCategory | null;
      confidence: number;
      ruleId?: string;
      ruleName?: string;
    }>('/categories/suggest', {
      transactionName,
      merchantName,
    });
    return {
      category: response.category ? transformCategory(response.category) : null,
      confidence: response.confidence,
      ruleId: response.ruleId,
      ruleName: response.ruleName,
    };
  },

  /**
   * Get frequently used categories (for quick selection)
   */
  getFrequentlyUsed: async (limit: number = 10): Promise<Category[]> => {
    const response = await api.get<{ categories: ApiCategory[] }>(
      '/categories/frequently-used',
      { limit }
    );
    return transformCategories(response.categories);
  },

  // ============================================================================
  // Category Preference Methods (WP-CAT-11)
  // API Endpoints: services/core/app/api/v1/categories.py lines 379-579
  // ============================================================================

  /**
   * Get categories with tenant-specific visibility preferences.
   *
   * @param includeDisabled - If true, returns all categories including disabled ones
   *                          If false (default), returns only enabled categories
   * @returns List of categories with isEnabled field indicating visibility preference
   *
   * API: GET /categories/with-preferences?include_disabled={bool}
   * Schema: CategoryWithPreferenceResponse[]
   */
  getWithPreferences: async (includeDisabled: boolean = true): Promise<CategoryWithPreference[]> => {
    const response = await api.get<ApiCategoryWithPreference[]>(
      '/categories/with-preferences',
      { includeDisabled }
    );
    return transformCategoriesWithPreferences(response);
  },

  /**
   * Update visibility/order preference for a single category.
   *
   * @param categoryId - Category UUID to update
   * @param isEnabled - Whether the category should be visible in dropdowns
   * @returns Update status
   *
   * API: PUT /categories/{id}/preference
   * Schema: PreferenceUpdateResponse
   */
  updatePreference: async (categoryId: string, isEnabled: boolean): Promise<PreferenceUpdateResponse> => {
    const response = await api.put<ApiPreferenceUpdateResponse>(
      `/categories/${categoryId}/preference`,
      { isEnabled }
    );
    return transformPreferenceUpdateResponse(response);
  },

  /**
   * Bulk update multiple category preferences.
   *
   * @param updates - Array of preference updates
   * @returns Update status with count
   *
   * API: POST /categories/preferences/bulk
   * Schema: PreferenceUpdateResponse
   */
  bulkUpdatePreferences: async (updates: BulkPreferenceUpdateItem[]): Promise<PreferenceUpdateResponse> => {
    const response = await api.post<ApiPreferenceUpdateResponse>(
      '/categories/preferences/bulk',
      updates
    );
    return transformPreferenceUpdateResponse(response);
  },

  /**
   * Reset category preferences to starter defaults.
   *
   * Clears all custom preferences and re-seeds with ~26 starter categories
   * enabled by default. Other categories will be disabled.
   *
   * @returns Reset status with count of categories configured
   *
   * API: POST /categories/preferences/reset
   * Schema: PreferenceUpdateResponse
   */
  resetPreferences: async (): Promise<PreferenceUpdateResponse> => {
    const response = await api.post<ApiPreferenceUpdateResponse>(
      '/categories/preferences/reset',
      {}
    );
    return transformPreferenceUpdateResponse(response);
  },
};

export default categoriesService;
